import { useCallback, useEffect, useState, type RefObject } from "react";

const SCROLL_TOLERANCE_PX = 1;

interface TabStripScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

export function useScrollableTabBarScroll(stripRef: RefObject<HTMLDivElement | null>): TabStripScrollState {
  const [scrollState, setScrollState] = useState<TabStripScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const maxScrollLeft = strip.scrollWidth - strip.clientWidth;
    const next: TabStripScrollState = {
      canScrollLeft: strip.scrollLeft > SCROLL_TOLERANCE_PX,
      canScrollRight: strip.scrollLeft < maxScrollLeft - SCROLL_TOLERANCE_PX,
    };
    setScrollState((previous) =>
      previous.canScrollLeft === next.canScrollLeft && previous.canScrollRight === next.canScrollRight
        ? previous
        : next,
    );
  }, [stripRef]);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const animationFrame = requestAnimationFrame(updateScrollState);
    strip.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(strip);
    if (strip.firstElementChild) resizeObserver.observe(strip.firstElementChild);

    return () => {
      cancelAnimationFrame(animationFrame);
      strip.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [stripRef, updateScrollState]);

  return scrollState;
}
